import { useState } from 'react';

interface Event {
  id: number;
  type: 'evento' | 'formacion' | 'publicacion';
  typeLabel: string;
  typeColor: string;
  date: string;
  dateShort: string;
  title: string;
  subtitle: string;
  description: string;
  location: string;
  organizers: string[];
  image: string;
  tags: string[];
  featured?: boolean;
  contact?: string[];
  isFlyer?: boolean;
}

const events: Event[] = [
  {
    id: 1,
    type: 'evento',
    typeLabel: 'Simposio',
    typeColor: 'bg-uri-teal text-white',
    date: 'Viernes, 27 de Junio de 2026',
    dateShort: '27 JUN 2026',
    title: '1er Simposio de Atención Domiciliaria',
    subtitle: 'Conferencistas nacionales e internacionales',
    description:
      'Conferencistas nacionales e internacionales compartirán sus experiencias y conocimientos en el campo de la atención domiciliaria. Un espacio de aprendizaje, networking y actualización profesional para todos los actores del sector salud.',
    location: 'Hotel Pestana — Caracas, Venezuela',
    organizers: ['Sociedad Anticancerosa de Venezuela', 'Colegio Universitario Jean Piaget', 'URI'],
    image: 'https://public.readdy.ai/ai/img_res/edited_2c564e984358a7336f06f3272413012e_dff31c86.jpg',
    tags: ['Atención Domiciliaria', 'Salud', 'Capacitación'],
    featured: true,
    contact: ['gerenciaeducacion@sociedadanticancerosa.org', 'uricamicami@gmail.com'],
  },
  {
    id: 2,
    type: 'formacion',
    typeLabel: 'Formación',
    typeColor: 'bg-uri-green text-white',
    date: '2026',
    dateShort: '2026',
    title: 'Programa de Formación en Enfermería',
    subtitle: 'Talleres especializados para profesionales de la salud',
    description:
      'Talleres prácticos de actualización profesional: Accesos Vasculares (técnicas avanzadas en acceso vascular) y Manejo de Catéter Port (uso, manipulación y mantenimiento del catéter puerto). Inversión: $60 por taller · Duración: 8 horas académicas cada taller.',
    location: 'Caracas, Venezuela',
    organizers: ['Unidad de Rehabilitación Integral (URI)', 'Colegio Universitario Jean Piaget (CUJP)', 'Sociedad Anticancerosa de Venezuela (SAV)'],
    image: 'https://storage.readdy-site.link/project_files/eb04eb51-6694-4b0d-a551-2bbe80661fc4/1089b898-b764-47ca-a7d4-1ce06b1e7050_uriweb1.jpeg?v=163c0c6ac91b9068c0459167d13993ee',
    tags: ['Enfermería', 'Accesos Vasculares', 'Catéter Port', 'Formación'],
    featured: false,
    isFlyer: true,
    contact: ['educacion@sociedadanticancerosa.org'],
  },
  {
    id: 3,
    type: 'formacion',
    typeLabel: 'Formación',
    typeColor: 'bg-uri-teal text-white',
    date: 'Martes, 24 de Marzo de 2026',
    dateShort: '24 MAR 2026',
    title: 'Programa de Formación en Enfermería: Accesos Vasculares',
    subtitle: 'Sociedad Anticancerosa de Venezuela · URI',
    description:
      'Taller especializado en técnicas avanzadas de acceso vascular para profesionales de la salud. Una jornada de actualización práctica organizada por la Sociedad Anticancerosa de Venezuela y la Unidad de Rehabilitación Integral URI.',
    location: 'Auditorio de la Clínica de Prevención, Av. Norte 3, entre Canónico y Esperanza',
    organizers: ['Sociedad Anticancerosa de Venezuela', 'Unidad de Rehabilitación Integral (URI)'],
    image: 'https://storage.readdy-site.link/project_files/eb04eb51-6694-4b0d-a551-2bbe80661fc4/d637d7b4-653b-4871-8805-8d89ebced9ac_uriweb2.jpeg?v=d96786d6c80a53142c84939c9e1de1df',
    tags: ['Enfermería', 'Accesos Vasculares', 'Formación', 'SAV'],
    featured: false,
    isFlyer: true,
    contact: ['gerenciaeducacion@sociedadanticancerosa.org', 'uricamicami@gmail.com'],
  },
];

const typeIcons: Record<string, string> = {
  evento: 'ri-calendar-event-line',
  formacion: 'ri-graduation-cap-line',
  publicacion: 'ri-book-open-line',
};

export default function NewsEventsSection() {
  const [activeEvent, setActiveEvent] = useState<Event | null>(null);

  return (
    <section id="noticias" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 bg-uri-teal/10 text-uri-teal text-sm font-semibold rounded-full mb-4 tracking-wide uppercase">
            Noticias & Eventos
          </span>
          <h3
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Últimas Actividades y Eventos
          </h3>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Capacitaciones, simposios y publicaciones en los que participa y patrocina nuestra empresa para el avance del cuidado domiciliario en Venezuela.
          </p>
        </div>

        {/* Events Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {events.map((event, index) => (
            <article
              key={event.id}
              className={`bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col group cursor-pointer ${
                index === 0 ? 'lg:col-span-1 ring-2 ring-uri-teal/30' : ''
              }`}
              onClick={() => setActiveEvent(event)}
            >
              {/* Image */}
              <div className="relative w-full h-52 overflow-hidden">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

                {/* Badge */}
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className={`px-3 py-1 text-xs font-bold rounded-full ${event.typeColor}`}>
                    {event.typeLabel}
                  </span>
                  {event.featured && (
                    <span className="px-3 py-1 text-xs font-bold rounded-full bg-amber-400 text-amber-900">
                      Destacado
                    </span>
                  )}
                </div>

                {/* Date chip */}
                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 text-center">
                  <p className="text-xs font-bold text-uri-teal tracking-widest">{event.dateShort}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${typeIcons[event.type]} text-uri-teal text-base`}></i>
                  </div>
                  <span className="text-xs text-gray-500 font-medium">{event.date}</span>
                </div>

                <h4 className="text-lg font-bold text-gray-900 mb-2 leading-snug group-hover:text-uri-teal transition-colors">
                  {event.title}
                </h4>
                <p className="text-sm text-uri-teal font-medium mb-3">{event.subtitle}</p>
                <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1 line-clamp-3">
                  {event.description}
                </p>

                {/* Location */}
                <div className="flex items-center gap-2 mb-4 text-gray-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line text-sm"></i>
                  </div>
                  <span className="text-xs">{event.location}</span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {event.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <button className="mt-auto w-full py-2.5 border-2 border-uri-teal text-uri-teal font-semibold rounded-lg hover:bg-uri-teal hover:text-white transition-all text-sm cursor-pointer whitespace-nowrap">
                  Ver Detalles
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Flyer Lightbox Modal */}
      {activeEvent && activeEvent.isFlyer && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setActiveEvent(null)}
        >
          <div
            className="relative max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setActiveEvent(null)}
              className="absolute -top-4 -right-4 z-10 w-9 h-9 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer shadow-lg"
            >
              <i className="ri-close-line text-gray-800 text-lg"></i>
            </button>

            {/* Full flyer image */}
            <img
              src={activeEvent.image}
              alt={activeEvent.title}
              className="w-full h-auto rounded-2xl object-contain"
            />

            {/* Contact strip below image */}
            {activeEvent.contact && (
              <div className="mt-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 flex flex-wrap items-center justify-center gap-3">
                {activeEvent.contact.map((email) => (
                  <a
                    key={email}
                    href={`mailto:${email}`}
                    className="flex items-center gap-1.5 text-white text-xs font-medium hover:text-uri-teal transition-colors"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-xs"></i>
                    </div>
                    {email}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Regular Detail Modal */}
      {activeEvent && !activeEvent.isFlyer && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setActiveEvent(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Image */}
            <div className="relative w-full h-64">
              <img
                src={activeEvent.image}
                alt={activeEvent.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <button
                onClick={() => setActiveEvent(null)}
                className="absolute top-4 right-4 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-800 text-lg"></i>
              </button>
              <div className="absolute bottom-4 left-6">
                <span className={`px-3 py-1 text-xs font-bold rounded-full ${activeEvent.typeColor}`}>
                  {activeEvent.typeLabel}
                </span>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              <p className="text-sm text-uri-teal font-semibold mb-2">{activeEvent.date}</p>
              <h3
                className="text-2xl font-bold text-gray-900 mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {activeEvent.title}
              </h3>
              <p className="text-base text-uri-teal font-medium mb-4">{activeEvent.subtitle}</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">{activeEvent.description}</p>

              {/* Location */}
              <div className="flex items-start gap-3 mb-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 flex items-center justify-center bg-uri-teal/10 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-uri-teal"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Lugar</p>
                  <p className="text-sm text-gray-800 font-semibold">{activeEvent.location}</p>
                </div>
              </div>

              {/* Organizers */}
              <div className="flex items-start gap-3 mb-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 flex items-center justify-center bg-uri-green/10 rounded-lg flex-shrink-0">
                  <i className="ri-building-line text-uri-green"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Organizadores</p>
                  <ul className="space-y-0.5">
                    {activeEvent.organizers.map((org) => (
                      <li key={org} className="text-sm text-gray-800 font-medium">
                        {org}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Contact if available */}
              {activeEvent.contact && (
                <div className="flex items-start gap-3 mb-6 p-4 bg-uri-teal/5 rounded-xl border border-uri-teal/20">
                  <div className="w-8 h-8 flex items-center justify-center bg-uri-teal/10 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-uri-teal"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Mayor información</p>
                    {activeEvent.contact.map((email) => (
                      <a
                        key={email}
                        href={`mailto:${email}`}
                        className="block text-sm text-uri-teal hover:underline font-medium"
                      >
                        {email}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => setActiveEvent(null)}
                className="w-full py-3 bg-uri-teal text-white font-semibold rounded-lg hover:bg-uri-teal-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
